import { r as registerTranslation, _ as __decorate, a as __metadata, b as BaseLocalizedComponent } from '../index-Blpj1lVH.js';
import { css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import 'lit/directive.js';

const translation$1 = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    /**
     * Suggerimento a11y annunciato dallo screen reader sul tab attivo e dismissibile.
     * Informa l'utente che può chiudere il tab riattivando l'elemento
     * (doppio tap con VoiceOver/TalkBack, Invio/Spazio con tastiera)
     * oppure premendo Delete/Backspace sulla tastiera fisica.
     */
    closeActiveTabHint: 'Puoi chiudere questo tab premendo Delete o Backspace da tastiera. Su dispositivi touch, doppio tap per chiudere questo tab.',
};

const translation = {
    $code: 'en',
    $name: 'English',
    $dir: 'ltr',
    /**
     * A11y hint announced by screen readers on the active + dismissible tab.
     * Tells the user they can close the tab by re-activating it
     * (double-tap on VoiceOver/TalkBack, Enter/Space on keyboard)
     * or by pressing Delete/Backspace on a physical keyboard.
     */
    closeActiveTabHint: 'You can close this tab by pressing Delete or Backspace on your keyboard. On touch devices, double tap to close this tab.',
};

var styles = css`:host {
  display: flex;
  height: var(--it-tab_height, auto);
  flex: 1 0 auto;
  align-items: center;
  justify-content: var(--it-tab_justify, center);
  padding: var(--it-tab_padding-y, var(--bsi-tab-padding-y)) var(--bsi-tab-padding-x);
  border-style: solid;
  border-radius: var(--it-tab_border-radius, 0);
  border-top-width: var(--it-tab_bw-top, 0);
  border-top-color: var(--it-tab_bc-top, transparent);
  border-right-width: var(--it-tab_bw-right, 0);
  border-right-color: var(--it-tab_bc-right, transparent);
  border-bottom: 1px solid var(--bsi-color-border-subtle);
  border-bottom-width: var(--it-tab_bw-bottom, var(--bsi-tab-border-width));
  border-bottom-color: var(--it-tab_bc-bottom, transparent);
  border-left-width: var(--it-tab_bw-left, 0);
  border-left-color: var(--it-tab_bc-left, transparent);
  appearance: none;
  background: var(--it-tab_bg, transparent);
  color: var(--bsi-tab-text-color, var(--bsi-color-text-secondary));
  cursor: pointer;
  font-family: inherit;
  font-size: var(--bsi-tab-font-size);
  font-weight: var(--bsi-tab-font-weight);
  gap: var(--it-tab_gap, 0);
  line-height: 1rem;
  opacity: var(--it-tab_opacity, 1);
  outline: none;
  text-decoration: none;
  white-space: var(--it-tab_white-space, nowrap);
  --bsi-tab-border-width: 3px;
  --bsi-tab-padding-x: var(--bsi-spacing-m, 1rem);
  --bsi-tab-padding-y: var(--bsi-spacing-s, 0.5rem);
  --bsi-tab-font-size: var(--bsi-label-font-size, 0.875rem);
  --bsi-tab-font-weight: 600;
  --bsi-tab-border-radius: var(--bsi-radius-smooth);
  --bsi-tab-shadow: 0 12px 25px -20px rgba(0, 0, 0, 0.5);
  --bsi-tab-background-light: var(--bsi-color-background-primary-lighter);
  --bsi-tab-border-width-cards: 1px;
  --bsi-tab-border-color-cards: var(--bsi-color-border-subtle);
}
@media (min-width: 992px) {
  :host {
    --bsi-tab-font-size: var(--bsi-label-font-size-l);
  }
}
:host ::slotted(it-icon) {
  --bsi-icon-default: var(--bsi-tab-icon-color, var(--bsi-icon-secondary));
}

:host(:hover) {
  --bsi-tab-text-color: var(--it-tab_hover-text-color, var(--bsi-color-text-secondary-hover));
  --bsi-tab-icon-color: var(--it-tab_hover-icon-color, var(--bsi-icon-secondary-hover));
  --it-tab_opacity: 1;
}

:host([active]) {
  position: relative;
  z-index: var(--it-tab_active-z-index, auto);
  border-top-color: var(--it-tab_active-bc-top, transparent);
  border-right-color: var(--it-tab_active-bc-right, transparent);
  border-bottom-width: var(--it-tab_active-bw-bottom, var(--it-tab_bw-bottom, var(--bsi-tab-border-width)));
  border-bottom-color: var(--it-tab_active-bc-bottom, var(--bsi-color-border-primary));
  border-left-color: var(--it-tab_active-bc-left, transparent);
  margin-bottom: var(--it-tab_active-mb-offset, 0px);
  background-color: var(--it-tab_active-bg, transparent) !important;
  color: var(--it-tab_color-active, var(--bsi-color-text-primary));
  --it-tab_opacity: 1;
}
:host([active]) ::slotted(it-icon) {
  --bsi-icon-default: var(--it-tab_icon-color-active, var(--bsi-icon-primary));
}

:host(:focus-visible) {
  box-shadow: inset 0 0 0 2px var(--bsi-color-outline-focus), inset 0 0 0 5px var(--bsi-color-border-inverse);
  outline: 3px solid transparent;
}

:host([disabled]) {
  color: var(--bsi-color-link-disabled);
  cursor: not-allowed;
  pointer-events: none;
}
:host([disabled]) ::slotted(it-icon) {
  --bsi-icon-default: var(--bsi-icon-disabled);
}
:host([disabled]) .it-tab-close {
  display: none !important;
}

:host([cards]) {
  height: 54px;
  border-radius: 4px 4px 0 0;
  border-top-width: 1px;
  border-top-color: transparent;
  border-right-width: 1px;
  border-right-color: transparent;
  border-bottom-width: 1px;
  border-bottom-color: transparent;
  border-left-width: 1px;
  border-left-color: transparent;
  background: var(--it-tab_cards-bg, var(--bsi-color-background-inverse));
}

:host([cards][dismissible]) {
  padding-right: 8px;
}

:host([cards][active]) {
  position: relative;
  z-index: 1;
  height: 55px;
  border-top-color: var(--it-tab_cards-border-color, var(--bsi-color-border-subtle));
  border-right-color: var(--it-tab_cards-border-color, var(--bsi-color-border-subtle));
  border-bottom-width: 0;
  border-bottom-color: transparent;
  border-left-color: var(--it-tab_cards-border-color, var(--bsi-color-border-subtle));
  margin-bottom: -1px;
  --it-tab_active-bg: var(--it-tab_cards-bg, var(--bsi-color-background-inverse));
}

.it-tab-close {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0.125rem;
  border-radius: 50%;
  color: currentColor;
  cursor: pointer;
  line-height: 1;
  text-decoration: none;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}
.it-tab-close:focus-visible {
  box-shadow: inset 0 0 0 2px var(--bsi-color-outline-focus), inset 0 0 0 5px var(--bsi-color-border-inverse);
  outline: 3px solid transparent;
}`;

registerTranslation(translation$1, translation);
/**
 * `it-tab` rappresenta il singolo tab (trigger) all'interno di un componente `it-tabs`.
 *
 * L'host stesso è l'elemento interattivo: porta `role="tab"`, `tabIndex` e tutti
 * gli attributi ARIA direttamente nel light DOM. Lo shadow DOM contiene solo
 * `<slot>` per il contenuto — nessun elemento interattivo annidato (no nested-interactive).
 *
 * ## Accessibilità dismissible
 *
 * Quando `dismissible` è attivo, il componente imposta automaticamente sull'host:
 *
 * - `aria-keyshortcuts="Delete Backspace"` — annuncia le scorciatoie da tastiera
 *   agli screen reader desktop (NVDA, JAWS, VoiceOver+Mac).
 * - `aria-description="Attiva di nuovo per chiudere."` (testo localizzato) — presente
 *   **solo** quando il tab è anche `active`; informa l'utente di screen reader mobile
 *   (VoiceOver+iOS, TalkBack+Android) che può chiudere il tab ri-attivando l'elemento
 *   (doppio tap nel DOM virtuale), senza bisogno di shortcut tastiera.
 *
 * Il click sull'host quando `active && dismissible` emette `it-tab-close-request`
 * **solo** se il gesto viene da touch o pen (doppio tap SR mobile).
 * Un click mouse sull'host non chiude il tab: l'utente usa la × esplicita.
 *
 * @slot - Etichetta del tab: testo, icona o icona+testo
 *
 * @fires it-tab-select - Emesso al click (non disabilitato, non già attivo+dismissibile).
 *   `detail.panel` contiene il nome del pannello associato.
 * @fires it-tab-close-request - Emesso al doppio-tap touch/pen quando `active && dismissible`
 *   (SR mobile), oppure dal pulsante × interno.
 *   `detail.panel` e `detail.type` (`'click'`) propagano verso `it-tabs`.
 */
let ItTab = class ItTab extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        /**
         * Nome del pannello `it-tab-panel` associato.
         * Deve corrispondere all'attributo `name` dell'`it-tab-panel` collegato.
         */
        this.panel = '';
        /** Se `true`, il tab è disabilitato e non interagibile. */
        this.disabled = false;
        /**
         * Stato attivo. Gestito dall'`it-tabs` padre.
         * Quando `true` il tab è selezionato e `tabIndex` dell'host è `0`.
         */
        this.active = false;
        /**
         * In variante card: impostato automaticamente dall'`it-tabs` padre quando `cards` è attivo.
         * Porta gli stili card direttamente su :host([cards]) in it-tab.scss.
         */
        this.cards = false;
        /**
         * Mostra il pulsante × per chiudere il tab.
         * Impostato automaticamente dall'`it-tabs` padre (da `dismissible` sul container);
         * non richiede manipolazione del light DOM (il bottone vive nello shadow DOM di `it-tab`).
         * Al click emette `it-tab-close-request` (bubbles + composed) verso `it-tabs`.
         */
        this.dismissible = false;
        /** Tipo di pointer dell'ultimo pointerdown: 'mouse' | 'touch' | 'pen' | '' */
        this._lastPointerType = '';
        this._handlePointerDown = (e) => {
            this._lastPointerType = e.pointerType;
        };
        this._handleClick = () => {
            if (this.disabled)
                return;
            // Tab già attivo + dismissibile: doppio tap touch/pen (SR mobile) → close-request.
            // Mouse click sull'host NON chiude: l'utente usa la × esplicita.
            if (this.dismissible && this.active && (this._lastPointerType === 'touch' || this._lastPointerType === 'pen')) {
                this.dispatchEvent(new CustomEvent('it-tab-close-request', {
                    bubbles: true,
                    composed: true,
                    detail: { panel: this.panel, type: 'click' },
                }));
                return;
            }
            this.dispatchEvent(new CustomEvent('it-tab-select', {
                bubbles: true,
                composed: true,
                detail: { panel: this.panel },
            }));
        };
        this._handleKeydown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                // Space/Enter attivano il tab, mai la chiusura.
                // La chiusura tramite tastiera è esclusiva di Delete/Backspace,
                // gestiti da it-tabs._onKeyDown.
                if (this.disabled || this.active)
                    return;
                this.dispatchEvent(new CustomEvent('it-tab-select', {
                    bubbles: true,
                    composed: true,
                    detail: { panel: this.panel },
                }));
            }
        };
        this._handleCloseClick = (e) => {
            e.stopPropagation(); // non triggerare selezione del tab
            this.dispatchEvent(new CustomEvent('it-tab-close-request', {
                bubbles: true,
                composed: true,
                detail: { panel: this.panel, type: e.type },
            }));
        };
    }
    /**
     * Restituisce l'`id` dell'host, usato dall'`it-tabs` padre per
     * impostare `aria-labelledby` sul pannello associato (tutto in light DOM,
     * nessun cross-shadow ARIA necessario).
     */
    get tabId() {
        return this.id || this._id || '';
    }
    /** Porta il focus sull'host. Chiamato dal `RovingTabindexController`. */
    focusTab() {
        this.focus();
    }
    connectedCallback() {
        super.connectedCallback?.();
        if (!this.hasAttribute('role'))
            this.setAttribute('role', 'tab');
        if (!this.id)
            this.id = this._id ?? '';
        this.setAttribute('aria-selected', String(this.active));
        this.setAttribute('aria-disabled', String(this.disabled));
        this.setAttribute('aria-controls', this.panel);
        // L'host è l'elemento interattivo: porta lui il tabindex del roving pattern.
        this.tabIndex = this.active ? 0 : -1;
        this._updateDismissibleAria();
        this.addEventListener('pointerdown', this._handlePointerDown);
        this.addEventListener('click', this._handleClick);
        this.addEventListener('keydown', this._handleKeydown);
    }
    disconnectedCallback() {
        super.disconnectedCallback?.();
        this.removeEventListener('pointerdown', this._handlePointerDown);
        this.removeEventListener('click', this._handleClick);
        this.removeEventListener('keydown', this._handleKeydown);
    }
    updated(changed) {
        super.updated?.(changed);
        if (changed.has('active')) {
            this.setAttribute('aria-selected', String(this.active));
            this.tabIndex = this.active ? 0 : -1;
        }
        if (changed.has('disabled'))
            this.setAttribute('aria-disabled', String(this.disabled));
        if (changed.has('panel'))
            this.setAttribute('aria-controls', this.panel);
        if (changed.has('active') || changed.has('dismissible'))
            this._updateDismissibleAria();
    }
    /**
     * Aggiorna gli attributi ARIA legati alla funzionalità dismissible:
     *
     * - `aria-keyshortcuts="Delete Backspace"` — presente sempre quando il tab è dismissibile;
     *   annuncia la scorciatoia da tastiera agli screen reader (NVDA, JAWS, VoiceOver+Mac).
     *
     * - `aria-description` — presente solo quando il tab è **sia attivo sia dismissibile**;
     *   contiene il testo localizzato (es. "Attiva di nuovo per chiudere.") che informa
     *   l'utente mobile (VoiceOver/TalkBack) che può chiudere ri-attivando l'elemento
     *   (doppio tap), oltre ai tasti Delete/Backspace già coperti da aria-keyshortcuts.
     */
    _updateDismissibleAria() {
        if (this.dismissible) {
            this.setAttribute('aria-keyshortcuts', 'Delete Backspace');
        }
        else {
            this.removeAttribute('aria-keyshortcuts');
        }
        if (this.dismissible && this.active) {
            this.setAttribute('aria-description', this.localize.term('closeActiveTabHint'));
        }
        else {
            this.removeAttribute('aria-description');
        }
    }
    render() {
        return html `
      <slot></slot>
      ${this.dismissible
            ? html `<span class="it-tab-close" aria-hidden="true" @click=${this._handleCloseClick}>
            <it-icon name="it-close"></it-icon>
          </span>`
            : ''}
    `;
    }
};
ItTab.styles = styles;
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItTab.prototype, "panel", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItTab.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItTab.prototype, "active", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItTab.prototype, "cards", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItTab.prototype, "dismissible", void 0);
ItTab = __decorate([
    customElement('it-tab')
], ItTab);

export { ItTab };
//# sourceMappingURL=it-tab.js.map
